import {skipToken} from '@reduxjs/toolkit/query';
import {
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  PostRmaRequestRequest,
  PostRmaRequestRequestSchema,
  usePostRmaRequestMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromCustomersAsSupplier,
  handleApiError,
  useGetSupplierCustomersQuery,
} from '@dms/shared';

import {composePath, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

export interface CreateRmaRequestFormProps extends TestIdProps {
  onClose: () => void;
}

export function CreateRmaRequestForm(props: CreateRmaRequestFormProps) {
  const navigate = useNavigate();

  const [postRmaRequest, {isLoading: isCreating}] = usePostRmaRequestMutation();

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: authorizationProfiles,
    isLoading: areAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery(isNotNil(tenant) ? {'x-tenant': tenant.id} : skipToken);

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSupplierCustomersQuery();

  const isLoading = isTenantLoading || areAuthorizationProfilesLoading || areSuppliersLoading;
  const isError = hasTenantError || hasAuthorizationProfilesError || hasSuppliersError;

  const handleSubmit: FormSubmitHandler<PostRmaRequestRequest> = async (values) => {
    await postRmaRequest(values)
      .unwrap()
      .then((data) => {
        props.onClose();
        navigate(
          composePath(warehouseRoutes.rmaRequestDetailOverview, {
            params: {id: data.rmaRequestId},
          })
        );
        showNotification.success(i18n.t('page.warehouse.notification.rmaRequestCreated'));
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} data-testid={suffixTestId('status', props)}>
      <Form<PostRmaRequestRequest>
        experimentalZodSchema={PostRmaRequestRequestSchema}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <Grid columns={2}>
              <FormField
                control={control}
                type="choice"
                name="supplierId"
                label={i18n.t('entity.warehouse.labels.supplier')}
                options={getOptionsFromCustomersAsSupplier(suppliers)}
                isRequired
                isNotClearable
                menuInPortal
                data-testid={suffixTestId('inputs.supplierId', props)}
              />
              <FormField
                control={control}
                type="choice"
                name="authorizationProfileId"
                label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                options={getOptionsFromAuthorizationProfiles(
                  authorizationProfiles,
                  formApi.watch('authorizationProfileId')
                )}
                isRequired
                isNotClearable
                menuInPortal
                data-testid={suffixTestId('inputs.authorizationProfileId', props)}
              />
            </Grid>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('actions.discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.create')}
                isLoading={isCreating}
                data-testid={suffixTestId('actions.create', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
